import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../api/client";
import { Input } from "../ui/input";
import { BRAND } from "../Brand";

const money = (v) => (v == null ? "—" : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
const idx = (v) => (v == null ? "—" : Number(v).toFixed(2));

function KpiCard({ label, value, hint, tone = "ink" }) {
  const tones = {
    ink: BRAND.colors.ink,
    blue: BRAND.colors.blue,
    green: BRAND.colors.green,
    orange: BRAND.colors.orange,
    red: "#EF4444",
  };
  return (
    <div className="bg-white border rounded-xl p-4 shadow-sm" style={{ borderTop: `4px solid ${tones[tone]}` }}>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono">{label}</div>
      <div className="mt-1 text-2xl font-black tracking-tight" style={{ color: tones[tone], fontFamily: "Chivo, sans-serif" }}>
        {value}
      </div>
      {hint ? <div className="text-[11px] text-neutral-500 mt-1">{hint}</div> : null}
    </div>
  );
}

// EVM screen: KPI cards + per-task table. Optionally uses a chosen baseline
// for PV planned% baseline reference.
export default function EvmTab({ projectId }) {
  const [data, setData] = useState(null);
  const [baselines, setBaselines] = useState([]);
  const [baselineId, setBaselineId] = useState("");
  const [statusDate, setStatusDate] = useState(new Date().toISOString().slice(0, 10));

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      const [{ data: bl }, { data: evm }] = await Promise.all([
        api.get("/plan/baselines", { params: { project_id: projectId } }),
        api.get("/plan/evm", {
          params: {
            project_id: projectId,
            status_date: statusDate,
            ...(baselineId ? { baseline_id: baselineId } : {}),
          },
        }),
      ]);
      setBaselines(bl.baselines || []);
      setData(evm);
    } catch {
      setData(null);
    }
  }, [projectId, statusDate, baselineId]);

  useEffect(() => { load(); }, [load]);

  const t = data?.totals || {};

  const cvTone = (t.CV ?? 0) < 0 ? "red" : "green";
  const svTone = (t.SV ?? 0) < 0 ? "red" : "green";
  const cpiTone = (t.CPI ?? 1) < 1 ? "red" : "green";
  const spiTone = (t.SPI ?? 1) < 1 ? "red" : "green";

  return (
    <div className="flex-1 overflow-auto p-6" data-testid="evm-tab">
      <div className="max-w-6xl">
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-neutral-600">Status date</label>
            <Input type="date" value={statusDate} onChange={(e) => setStatusDate(e.target.value)} className="h-9 mt-1 w-44" data-testid="evm-status-date" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-neutral-600">Baseline (optional)</label>
            <select value={baselineId} onChange={(e) => setBaselineId(e.target.value)} className="mt-1 h-9 border border-neutral-200 rounded px-2 text-sm bg-white min-w-[200px]" data-testid="evm-baseline-select">
              <option value="">None (use current schedule)</option>
              {baselines.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="evm-kpis">
          <KpiCard label="BAC — Budget" value={money(t.BAC)} hint="Sum of planned costs" tone="ink" />
          <KpiCard label="PV — Planned Value" value={money(t.PV)} hint="Work planned to date" tone="blue" />
          <KpiCard label="EV — Earned Value" value={money(t.EV)} hint="Work actually done" tone="green" />
          <KpiCard label="AC — Actual Cost" value={money(t.AC)} hint="Money spent so far" tone="orange" />
          <KpiCard label="CV — Cost Variance" value={money(t.CV)} hint={cvTone === "red" ? "Over budget" : "Under budget"} tone={cvTone} />
          <KpiCard label="SV — Schedule Variance" value={money(t.SV)} hint={svTone === "red" ? "Behind schedule" : "Ahead / on plan"} tone={svTone} />
          <KpiCard label="CPI — Cost Index" value={idx(t.CPI)} hint={cpiTone === "red" ? "> $1 spent per $1 earned" : "Efficient"} tone={cpiTone} />
          <KpiCard label="SPI — Schedule Index" value={idx(t.SPI)} hint={spiTone === "red" ? "Slower than plan" : "On/ahead of plan"} tone={spiTone} />
        </div>

        <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="evm-task-table">
              <thead className="bg-neutral-50 border-b border-neutral-200">
                <tr className="text-[10px] uppercase tracking-widest text-neutral-500">
                  <th className="text-left px-3 py-2">Task</th>
                  <th className="text-right px-3 py-2 w-16">%</th>
                  <th className="text-right px-3 py-2 w-24">BAC</th>
                  <th className="text-right px-3 py-2 w-24">PV</th>
                  <th className="text-right px-3 py-2 w-24">EV</th>
                  <th className="text-right px-3 py-2 w-24">AC</th>
                  <th className="text-right px-3 py-2 w-20">CPI</th>
                  <th className="text-right px-3 py-2 w-20">SPI</th>
                </tr>
              </thead>
              <tbody>
                {(!data || data.rows.length === 0) ? (
                  <tr><td colSpan={8} className="text-center py-8 text-neutral-400 text-sm">No plan tasks yet.</td></tr>
                ) : data.rows.map((r) => (
                  <tr key={r.id} className={`border-b border-neutral-100 ${r.is_summary ? "bg-neutral-50 font-semibold" : ""}`} data-testid={`evm-row-${r.id}`}>
                    <td className="px-3 py-2 text-neutral-800">{r.name}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.percent_complete}%</td>
                    <td className="px-3 py-2 text-right font-mono text-neutral-700">{money(r.bac)}</td>
                    <td className="px-3 py-2 text-right font-mono text-blue-700">{money(r.pv)}</td>
                    <td className="px-3 py-2 text-right font-mono text-emerald-700">{money(r.ev)}</td>
                    <td className="px-3 py-2 text-right font-mono text-orange-700">{money(r.ac)}</td>
                    <td className={`px-3 py-2 text-right font-mono ${r.cpi != null && r.cpi < 1 ? "text-red-600" : "text-neutral-600"}`}>{idx(r.cpi)}</td>
                    <td className={`px-3 py-2 text-right font-mono ${r.spi != null && r.spi < 1 ? "text-red-600" : "text-neutral-600"}`}>{idx(r.spi)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
